import { useEffect, useMemo, useState } from "react";
import { Smartphone, Monitor, Share2, MoreVertical, Bookmark, Check, Copy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const PLATFORM_NAME = "Aesthetic OS";

// ------------------------------- Rilevamento -------------------------------
function detect() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const u = ua.toLowerCase();
  const isIPad = /ipad/.test(u) || (/(macintosh)/.test(u) && "ontouchend" in (globalThis.document || {}));
  const isIPhone = /iphone|ipod/.test(u);
  const isAndroid = /android/.test(u);
  const isMobile = isIPhone || isIPad || isAndroid || /mobile/.test(u);
  const isMac = /macintosh|mac os x/.test(u) && !isIPad;
  const isWindows = /windows/.test(u);
  const isLinux = /linux/.test(u) && !isAndroid;

  let os = "Sconosciuto";
  if (isIPhone) os = "iPhone";
  else if (isIPad) os = "iPad";
  else if (isAndroid) os = "Android";
  else if (isMac) os = "macOS";
  else if (isWindows) os = "Windows";
  else if (isLinux) os = "Linux";

  let browser = "Browser";
  if (/edg\//.test(u)) browser = "Edge";
  else if (/samsungbrowser/.test(u)) browser = "Samsung Internet";
  else if (/opr\//.test(u) || /opera/.test(u)) browser = "Opera";
  else if (/firefox|fxios/.test(u)) browser = "Firefox";
  else if (/chrome|crios/.test(u)) browser = "Chrome";
  else if (/safari/.test(u)) browser = "Safari";

  const isDesktop = !isMobile;
  return { os, browser, isMobile, isDesktop, isIPhone, isIPad, isAndroid, isMac };
}

// ------------------------------- Istruzioni --------------------------------
// Nessuna promessa di install automatica dove il browser/OS non lo permette.
function buildSteps({ os, browser, isDesktop, isIPhone, isIPad, isAndroid }, canInstallPWA) {
  // iOS: solo Safari installa davvero l'icona sulla Home. Altri browser iOS
  // aprono comunque in Safari o sfruttano lo stesso menu Condividi.
  if (isIPhone || isIPad) {
    if (browser === "Safari") {
      return {
        title: "Aggiungi alla schermata Home (Safari)",
        icon: Share2,
        steps: [
          "Tocca il pulsante Condividi in basso al centro (icona quadrato con freccia verso l'alto).",
          "Scorri e scegli \"Aggiungi a schermata Home\".",
          `Conferma il nome (${PLATFORM_NAME}) e tocca \"Aggiungi\".`,
        ],
        note: "Su iPhone/iPad solo Safari può creare l'icona sulla Home. Se stai usando un altro browser, apri prima questa pagina in Safari.",
      };
    }
    return {
      title: `Apri in Safari per aggiungere alla Home (${browser} su ${os})`,
      icon: Share2,
      steps: [
        "Copia il link della pagina.",
        "Apri Safari e incolla il link nella barra degli indirizzi.",
        "Tocca Condividi (freccia verso l'alto) → \"Aggiungi a schermata Home\".",
      ],
      note: `Su ${os}, ${browser} non può creare direttamente l'icona sulla Home. Il passaggio da Safari è necessario.`,
    };
  }

  // Android
  if (isAndroid) {
    if (browser === "Chrome" || browser === "Edge" || browser === "Opera") {
      return {
        title: `Aggiungi alla schermata Home (${browser})`,
        icon: MoreVertical,
        steps: [
          "Tocca il menu con i tre puntini in alto a destra.",
          "Scegli \"Installa app\" oppure \"Aggiungi a schermata Home\".",
          "Conferma il nome e tocca \"Aggiungi\" o \"Installa\".",
        ],
        note: canInstallPWA ? "Se il tuo dispositivo lo consente, puoi installarla come app dedicata." : null,
      };
    }
    if (browser === "Samsung Internet") {
      return {
        title: "Aggiungi alla schermata Home (Samsung Internet)",
        icon: MoreVertical,
        steps: [
          "Tocca l'icona menu in basso (tre linee o punti).",
          "Scegli \"Aggiungi pagina a\" → \"Schermata Home\".",
          "Conferma il nome e tocca \"Aggiungi\".",
        ],
      };
    }
    if (browser === "Firefox") {
      return {
        title: "Aggiungi alla schermata Home (Firefox)",
        icon: MoreVertical,
        steps: [
          "Tocca il menu con i tre puntini a destra della barra indirizzi.",
          "Scegli \"Installa\" oppure \"Aggiungi alla schermata Home\".",
          "Conferma il nome e tocca \"Aggiungi\".",
        ],
      };
    }
  }

  // Desktop
  if (isDesktop) {
    if (browser === "Chrome" || browser === "Edge") {
      return {
        title: `Aggiungi ai preferiti o installa come app (${browser})`,
        icon: canInstallPWA ? Download : Bookmark,
        steps: canInstallPWA
          ? [
              "Vedi un'icona \"Installa\" nella barra degli indirizzi? Fai clic e conferma.",
              "Altrimenti apri il menu con i tre puntini → \"Installa Aesthetic OS…\" o \"App\" → \"Installa questo sito\".",
              "Per un preferito nella barra: premi Ctrl+D (Cmd+D su Mac) e conferma.",
            ]
          : [
              "Premi Ctrl+D (Cmd+D su Mac) per aprire il menu preferiti e conferma.",
              "Se compare un'icona \"Installa\" nella barra indirizzi, puoi anche installarla come app.",
              "Puoi trascinare l'URL della barra sul Desktop per creare un collegamento.",
            ],
      };
    }
    if (browser === "Safari") {
      return {
        title: "Aggiungi ai preferiti (Safari Mac)",
        icon: Bookmark,
        steps: [
          "Premi Cmd+D oppure vai su \"Preferiti\" → \"Aggiungi Preferito\".",
          "Scegli la barra dei Preferiti come cartella per averlo sempre a portata.",
          "Puoi anche trascinare l'URL dalla barra sul Desktop per creare un collegamento.",
        ],
        note: "Safari desktop non installa app dedicate: usa Preferiti o un collegamento sul Desktop.",
      };
    }
    if (browser === "Firefox") {
      return {
        title: "Aggiungi ai preferiti (Firefox)",
        icon: Bookmark,
        steps: [
          "Premi Ctrl+D (Cmd+D su Mac) per aprire il menu preferiti e conferma.",
          "Salva nella \"Barra dei preferiti\" per accesso rapido.",
          "Puoi trascinare l'URL dalla barra sul Desktop per creare un collegamento.",
        ],
        note: "Firefox desktop non installa app dedicate: usa Preferiti.",
      };
    }
  }

  // Fallback generico
  return {
    title: "Aggiungi ai preferiti o alla schermata Home",
    icon: Bookmark,
    steps: [
      "Apri il menu del tuo browser (di solito tre puntini o linee).",
      "Cerca \"Aggiungi ai preferiti\", \"Preferiti\" o \"Aggiungi a schermata Home\".",
      "Conferma il nome e salva.",
    ],
    note: "Le voci esatte cambiano a seconda del browser. Se non trovi l'opzione, cerca \"segnalibro\" o \"bookmark\".",
  };
}

// ---------------------------------- Dialog ---------------------------------
export function AddToDeviceDialog({ open, onOpenChange, clientName }) {
  const [dev, setDev] = useState(() => detect());
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const currentUrl = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    if (!open) return;
    setDev(detect());
    const onBeforeInstall = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [open]);

  const info = useMemo(() => buildSteps(dev, !!deferred), [dev, deferred]);
  const Icon = info.icon;

  const triggerInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        toast.success("Installazione avviata");
        setInstalled(true);
      }
      setDeferred(null);
    } catch {
      toast.error("Installazione non disponibile in questo browser");
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const title = clientName
    ? `Aggiungi ${PLATFORM_NAME} — ${clientName} al tuo dispositivo`
    : `Aggiungi ${PLATFORM_NAME} al tuo dispositivo`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(32rem,calc(100vw-1.5rem))] max-h-[calc(100dvh-2rem)] overflow-y-auto overflow-x-hidden p-4 sm:p-6 [&_*]:min-w-0" data-testid="add-to-device-dialog">
        <DialogHeader className="min-w-0">
          <DialogTitle className="flex min-w-0 items-start gap-2 pr-6 text-left text-base sm:text-lg">
            <img src="/apple-touch-icon.png" alt="" className="mt-0.5 h-7 w-7 shrink-0 rounded-lg sm:h-8 sm:w-8" />
            <span className="min-w-0 flex-1 leading-snug [overflow-wrap:anywhere]">{title}</span>
          </DialogTitle>
          <DialogDescription className="text-sm [overflow-wrap:anywhere]">
            Crea un accesso rapido a {PLATFORM_NAME} sulla schermata Home, sul desktop o nei preferiti.
            Nessuna app da scaricare, nessun nuovo login.
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-3 sm:space-y-4">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs" data-testid="add-to-device-detect">
            {dev.isMobile ? <Smartphone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <Monitor className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            <span className="text-muted-foreground">Rilevato:</span>
            <span className="font-medium text-foreground [overflow-wrap:anywhere]">{dev.os} · {dev.browser}</span>
          </div>

          <div className="min-w-0 rounded-xl border border-border/60 p-3 sm:p-4" data-testid="add-to-device-steps">
            <div className="flex min-w-0 items-start gap-2 text-sm font-semibold text-foreground">
              <Icon className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 leading-snug [overflow-wrap:anywhere]">{info.title}</span>
            </div>
            <ol className="mt-3 space-y-2 text-sm text-foreground">
              {info.steps.map((s, i) => (
                <li key={i} className="flex min-w-0 gap-2.5" data-testid={`add-to-device-step-${i}`}>
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-[11px] font-semibold text-foreground">{i + 1}</span>
                  <span className="min-w-0 flex-1 leading-relaxed [overflow-wrap:anywhere]">{s}</span>
                </li>
              ))}
            </ol>
            {info.note && (
              <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] leading-relaxed text-amber-700 dark:text-amber-400 [overflow-wrap:anywhere]" data-testid="add-to-device-note">
                {info.note}
              </p>
            )}
          </div>

          {deferred && !installed && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-xs" data-testid="add-to-device-pwa-hint">
              <span className="min-w-0 break-words text-emerald-700 dark:text-emerald-400">Il tuo browser supporta l&apos;installazione come app.</span>
              <Button size="sm" onClick={triggerInstall} data-testid="add-to-device-install">
                <Download className="mr-1.5 h-3.5 w-3.5" />Installa
              </Button>
            </div>
          )}

          {installed && (
            <div className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-400" data-testid="add-to-device-installed">
              <Check className="h-3.5 w-3.5 shrink-0" /> Installata. Trovala tra le tue app.
            </div>
          )}

          <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs" data-testid="add-to-device-url">
            <span className="shrink-0 text-muted-foreground">Link:</span>
            <span className="min-w-0 flex-1 truncate text-foreground" title={currentUrl}>{currentUrl}</span>
            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={copyUrl} data-testid="add-to-device-copy" title="Copia link">
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </div>

        <DialogFooter className="mt-2 sm:mt-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="add-to-device-close">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
